import { useCallback, useMemo, useState } from "react";

import dayjs from "dayjs";
import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes, elevation } from "@similarweb/styles";
import { Button, TabPanel, Tabs } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { toggleCreateUpdateSegmentGroupModal, toggleDeleteSegmentGroupModal } from "app/actions/segmentsModuleActions";
import type {
    ICustomSegment,
    ICustomSegmentAccount,
    ICustomSegmentsGroup,
} from "app/services/segments/segmentsApiService";
import { SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { MODE } from "../../analysis/SegmentsAnalysisTrafficContainerMode";
import { DISABLED_TOOLTIP, DISABLED_TOOLTIP_SHARED } from "../../components/convert-into-industry-btn/consts";
import ConvertIntoIndustryBtn from "../../components/convert-into-industry-btn/ConvertIntoIndustryBtn";
import CustomIndustryModal from "../../components/custom-industry-modal/CustomIndustryModal";
import {
    ContentContainer,
    StyledTab,
    TabListStyled,
    TabsContainer,
} from "../../components/SegmentsSharedStyledComponents";
import { SegmentsStartTableAdditionalOptionsEllipsis } from "../../components/SegmentsStartTableAdditionalOptionsEllipsis";
import ViewRelatedIndustryBtn from "../../components/view-related-industry-btn/ViewRelatedIndustryBtn";

import { MAX_SEGMENTS_ALLOWED_FOR_COMPARISON, MAX_VISIBLE_COMPARISON_MEMBER_ICONS } from "./constants";
import { SegmentsComparisonTable } from "./SegmentsComparisonTable";
import type { ComparisonRow } from "./types";

interface ISegmentsComparisonContainerProps {
    segments: ICustomSegment[];
    organizationSegments: ICustomSegmentAccount[];
    groups: ICustomSegmentsGroup[];
    isLoading: boolean;
}

export const SegmentsComparisonContainer = ({
    segments,
    organizationSegments,
    groups,
    isLoading,
}: ISegmentsComparisonContainerProps) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const [selectedTab, setSelectedTab] = useState(0);
    const [showConvertIntoIndustryModal, setShowConvertIntoIndustryModal] = useState(false);
    const [industryModalSegmentsGroup, setIndustryModalSegmentsGroup] = useState({ name: "", id: "" });

    const handleCreateGroup = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "segments/start-page/comparison-table/create-group");
        dispatch(toggleCreateUpdateSegmentGroupModal(true));
    }, [dispatch]);

    const segmentIds = useMemo(() => new Set(segments.map((segment) => segment.id)), [segments]);

    const isSharedWithMe = useCallback(
        (memberKeys: string[]) =>
            memberKeys.some((key) => {
                const [id, type] = SegmentsUtils.getSegmentIdTypeByKey(key);
                return !segmentIds.has(id) && type !== SEGMENT_TYPES.WEBSITE;
            }),
        [segmentIds],
    );

    const tableData = useMemo(() => {
        if (groups) {
            return groups
                .toSorted((left, right) => (dayjs(left.creationTime).isBefore(right.creationTime) ? 1 : -1))
                .map((group) => {
                    const availableMembers = { segments: organizationSegments, websites: group.websites };
                    const comparisonMembers = group.members.map((memberKey, index) =>
                        index <= MAX_VISIBLE_COMPARISON_MEMBER_ICONS
                            ? SegmentsUtils.getSegmentObjectByKey(memberKey, availableMembers)[0]
                            : { domain: "", favicon: "" },
                    );

                    return {
                        id: group.id,
                        name: group.name,
                        comparisonMembers,
                        membersCount: group.members.length,
                        customIndustryId: group?.customIndustryId,
                        isContainsSharedWithMe: isSharedWithMe(group.members),
                    };
                });
        }
        return [];
    }, [groups, organizationSegments, isSharedWithMe]);

    const myComparisons = useMemo(() => tableData.filter((row) => !row.customIndustryId), [tableData]);

    const convertedComparisons = useMemo(() => tableData.filter((row) => !!row.customIndustryId), [tableData]);

    const handleRowClick = useCallback((row: ComparisonRow) => {
        allTrackers.dispatchTrackEvent("Internal link", "click", "SegmentAnalysis/Analyze segments group", {
            /* eslint-disable-next-line @typescript-eslint/naming-convention -- tracking API requires PascalCase */
            SegmentComparison_ID: row.id,
        });
        const currentModule = proNavigator.getCurrentModule();
        void proNavigator.go(`${currentModule}-analysis-traffic`, {
            id: row.id,
            country: 840,
            mode: MODE.group,
            duration: "3m",
            ...SegmentsUtils.getPageFilterParams(row.id),
        });
    }, []);

    const handleConvertIntoIndustryBtnClick = useCallback(({ name, id }: { name: string; id: string }) => {
        allTrackers.dispatchTrackEvent("Button", "click", "View in market analysis/create industry");
        setShowConvertIntoIndustryModal(true);
        setIndustryModalSegmentsGroup({ name, id });
    }, []);

    const handleEditGroup = useCallback(
        (row: ComparisonRow) => {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `segments/edit/segment/modal/${row.id}`);
            const segmentGroup = SegmentsUtils.getSegmentGroupById(groups, row.id);
            dispatch(toggleCreateUpdateSegmentGroupModal(true, segmentGroup));
        },
        [dispatch, groups],
    );

    const handleDeleteGroup = useCallback(
        (row: ComparisonRow) => {
            allTrackers.dispatchTrackEvent("Button", "click", `segments/delete/segment/modal/${row.id}`);
            const segmentGroup = SegmentsUtils.getSegmentGroupById(groups, row.id);
            dispatch(toggleDeleteSegmentGroupModal(true, segmentGroup));
        },
        [dispatch, groups],
    );

    const rowActionRenderers = useMemo(
        () => [
            (actionProps: { row: ComparisonRow }) => {
                const actionRow = actionProps.row;
                const shouldHideConvertBtn =
                    actionRow.membersCount > MAX_SEGMENTS_ALLOWED_FOR_COMPARISON || actionRow.isContainsSharedWithMe;
                const disabledTooltip = actionRow.isContainsSharedWithMe ? DISABLED_TOOLTIP_SHARED : DISABLED_TOOLTIP;

                if (shouldHideConvertBtn && !actionRow.customIndustryId) {
                    return null;
                }

                if (actionRow.customIndustryId) {
                    return (
                        <ViewRelatedIndustryBtn
                            customIndustryId={actionRow.customIndustryId}
                            trackingEvent={() =>
                                allTrackers.dispatchTrackEvent(
                                    "Button",
                                    "click",
                                    "View in market analysis/access industry",
                                )
                            }
                        />
                    );
                }

                return (
                    <ConvertIntoIndustryBtn
                        segmentGroupProps={{ id: actionRow.id, name: actionRow.name }}
                        onClick={handleConvertIntoIndustryBtnClick}
                        disabledTooltip={disabledTooltip}
                    />
                );
            },
            (rendererProps: { row: ComparisonRow }) => (
                <SegmentsStartTableAdditionalOptionsEllipsis
                    row={rendererProps.row}
                    groupEllipsis
                    onClickDelete={handleDeleteGroup}
                    onClickEdit={handleEditGroup}
                />
            ),
        ],
        [handleConvertIntoIndustryBtnClick, handleDeleteGroup, handleEditGroup],
    );

    const handleTabSelect = useCallback((index: number) => {
        setSelectedTab(index);
        allTrackers.dispatchTrackEvent("Tab", "switch", "My comparisonsConverted comparisons");
    }, []);

    const myComparisonsNoData = useMemo(
        () => ({
            title: translate("segments.analysis.organization.my.comparisons.no.data.with.market.conversion.title"),
            subtitle: translate(
                "segments.analysis.organization.my.comparisons.no.data.with.market.conversion.subtitle",
            ),
        }),
        [translate],
    );

    const convertedComparisonsNoData = useMemo(
        () => ({
            title: translate("segments.analysis.organization.no.data.title"),
            subtitle: translate("segments.analysis.organization.no.data.subtitle"),
        }),
        [translate],
    );

    return (
        <Box
            background={colorsPalettes.carbon[0]}
            borderRadius="5px"
            boxShadow={elevation.Z1}
            data-automation="segments-new-comparison-section"
        >
            <TabsContainer>
                <TabListStyled>
                    <StyledTab
                        className="firstTab"
                        selected={selectedTab === 0}
                        data-automation="segments-new-comparison-tab-my"
                        onClick={() => handleTabSelect(0)}
                    >
                        {translate("segments.analysis.homepage.group.tab")}
                    </StyledTab>
                    <StyledTab
                        className="secondTab"
                        selected={selectedTab === 1}
                        data-automation="segments-new-comparison-tab-converted"
                        tooltipText={translate("segments.analysis.homepage.group.tab.converted.comparisons.tooltip")}
                        onClick={() => handleTabSelect(1)}
                    >
                        {translate("segments.analysis.homepage.group.tab.converted.comparisons")}
                    </StyledTab>
                </TabListStyled>
                <Flex justifyContent="space-around">
                    <Button
                        isDisabled={segments?.length <= 1 ? organizationSegments?.length <= 1 : null}
                        data-automation="segments-new-comparison-create-group"
                        type="outlined"
                        onClick={handleCreateGroup}
                    >
                        {translate("segment.startpage.comparision.new.compare")}
                    </Button>
                </Flex>
            </TabsContainer>
            <ContentContainer>
                <Tabs
                    forceRenderTabPanel={false}
                    selectedIndex={selectedTab}
                    onSelect={handleTabSelect}
                >
                    <TabPanel className="firstTab">
                        <SegmentsComparisonTable
                            data={myComparisons}
                            isLoading={isLoading}
                            onRowClick={handleRowClick}
                            rowActionRenderers={rowActionRenderers}
                            noData={myComparisonsNoData}
                        />
                    </TabPanel>
                    <TabPanel className="secondTab">
                        <SegmentsComparisonTable
                            data={convertedComparisons}
                            isLoading={isLoading}
                            onRowClick={handleRowClick}
                            rowActionRenderers={rowActionRenderers}
                            noData={convertedComparisonsNoData}
                        />
                    </TabPanel>
                </Tabs>
                <CustomIndustryModal
                    showModal={showConvertIntoIndustryModal}
                    setShowModal={setShowConvertIntoIndustryModal}
                    segmentGroupName={industryModalSegmentsGroup.name}
                    segmentGroupId={industryModalSegmentsGroup.id}
                />
            </ContentContainer>
        </Box>
    );
};
